import {Observable, ReplaySubject, Subject} from "rxjs";
import {
    addNoteChange,
    ClientNotesChange, editNoteChange,
    isAddNoteChange,
    isEditNoteChange,
    isRefreshChange,
    isRemoveNoteChange, refreshNotesChange, removeNoteChange
} from "./ClientNotesChange";
import {ClientNotesRecords} from "../../../models/ClientNotesRecord";
import {ClientNotesService} from "../service/ClientNotesService";

export class ClientNotesViewStore {

    private readonly accountId$ = new ReplaySubject<number>(1);
    private readonly changes$ = new Subject<ClientNotesChange>();
    private readonly notes$: Observable<ClientNotesRecords>;

    public constructor(private readonly service: ClientNotesService) {
        this.notes$ = this.buildNotes();
    }

    private buildNotes(): Observable<ClientNotesRecords> {
        return this.accountId$
                   .switchMap((accountId) => this.watchChanges(accountId))
                   .publishReplay(1)
                   .refCount();
    }

    private watchChanges(accountId: number): Observable<ClientNotesRecords> {
        return this.changes$
                   .startWith(refreshNotesChange())
                   .concatMap((c) => this.processChange(c, accountId))
                   .auditTime(100)
                   .flatMap(() => this.retrieveNotes(accountId));
    }

    private processChange(change: ClientNotesChange, accountId: number): Observable<boolean> {
        if (isRefreshChange(change)) {
            return Observable.of(true);
        } else if (isAddNoteChange(change)) {
            return Observable.of(change)
                             .flatMap((c) => this.service.create(c.content, c.noteTag, accountId))
                             .map(() => true)
                             .catch((err) => Observable.empty());
        } else if (isRemoveNoteChange(change)) {
            return Observable.of(change.id)
                             .flatMap((id) => this.service.remove(id, accountId))
                             .map(() => true)
                             .catch((err) => Observable.empty());
        } else if (isEditNoteChange(change)) {
            return Observable.of(change)
                             .flatMap((c) => this.service.update(c.noteId, accountId, c.content, c.noteTag))
                             .map(() => true)
                             .catch((err) => Observable.empty());
        } else {
            return Observable.of(true);
        }
    }

    private retrieveNotes(accountId: number): Observable<ClientNotesRecords> {
        return Observable.of(accountId)
                         .flatMap((id) => this.service.list(id))
                         .catch((err) => Observable.empty());
    }

    public updateAccountId(id: number) {
        this.accountId$.next(id);
    }

    public addNote(content: string, noteTag: string) {
        const change = addNoteChange(content, noteTag);
        this.changes$.next(change);
    }

    public removeNote(id: number) {
        const change = removeNoteChange(id);
        this.changes$.next(change);
    }

    public editNote(noteId: number, content: string, noteTag: string) {
        const change = editNoteChange(noteId, content, noteTag);
        this.changes$.next(change);
    }

    public get notes(): Observable<ClientNotesRecords> {
            return this.notes$;
    }
}
